import React, { useEffect, useState } from 'react';
import { IFilePageContent } from '../MobileAppTypes';
import { IScreenEditorProps } from './SharedTypes';
import { EasyFileInput } from '../../Tools/ExpandableDiv';

interface IFilePageEditorProps extends IScreenEditorProps {
  content: IFilePageContent;
}

const FilePageEditor: React.FC<IFilePageEditorProps> = ({
  content,
  errors,
  onContentChange,
  onFilesChange,
  onValidationChange
}) => {
  const [localContent, setLocalContent] = useState<IFilePageContent>(content);
  const [localFiles, setLocalFiles] = useState<Record<string, File>>({});

  useEffect(() => {
    setLocalContent(content);
  }, [content]);

  const updateContent = (updates: Partial<IFilePageContent>) => {
    const newContent = { ...localContent, ...updates };
    setLocalContent(newContent);
    onContentChange(newContent);
    
    // Validate on change
    const validationErrors = validate(newContent);
    onValidationChange(validationErrors);
  };

  const fileTypeOptions: IFilePageContent['fileType'][] = ['pdf', 'image', 'text', 'video'];

  const getAllowedExts = (fileType: IFilePageContent['fileType']): string[] => {
    const extString =
      fileType === 'pdf' ? '.pdf' :
      fileType === 'image' ? '.jpg,.jpeg,.png,.gif,.svg,.webp,.heic' :
      fileType === 'video' ? '.mp4' :
      '.txt,.doc,.docx';
    return extString.split(',');
  };

  // Only known extensions from another managed type count as a mismatch - an
  // extension-less or unrecognized display name (users can freely rename this field) is not an error.
  const getFileTypeFromExt = (ext: string): IFilePageContent['fileType'] | undefined => {
    return fileTypeOptions.find((t) => getAllowedExts(t).includes(ext));
  };

  const validate = (contentToValidate: IFilePageContent = localContent): string[] => {
    const errors: string[] = [];
    if (!contentToValidate.fileUrl) errors.push('File must be uploaded');
    if (!contentToValidate.fileName) errors.push('File name is required');

    const lastDot = contentToValidate.fileName?.lastIndexOf('.') ?? -1;
    if (lastDot > 0) {
      const ext = contentToValidate.fileName.substring(lastDot).toLowerCase();
      const actualType = getFileTypeFromExt(ext);
      if (actualType && actualType !== contentToValidate.fileType) {
        errors.push(`Selected file type does not match the attached file (${ext}). Remove or replace the file, or choose the matching file type.`);
      }
    }

    return errors;
  };

  const handleFileSelect = (file: File) => {
    const newFiles = { ...localFiles, screen_file: file };
    setLocalFiles(newFiles);
    onFilesChange(newFiles);
    updateContent({ 
      fileName: file.name, 
      fileUrl: 'Pending Upload' 
    });
  };

  return (
    <div className="form-section">
      <h4>File Page Content</h4>
      <div className="form-horizontal">
        <div className="form-group">
          <label className="col-sm-3 control-label">File Type</label>
          <div className="col-sm-9">
            <select 
              className="form-control" 
              value={localContent.fileType} 
              onChange={(e) => updateContent({ 
                fileType: e.target.value as 'pdf' | 'image' | 'text' | 'video' 
              })}
            >
              <option value="pdf">PDF Document</option>
              <option value="image">Image</option>
              <option value="text">Text Document</option>
              <option value="video">Video</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label">File</label>
          <div className="col-sm-9">
            <EasyFileInput
              htmlId="screen-file-upload"
              exts={getAllowedExts(localContent.fileType).join(',')}
              fileSize={localContent.fileType === 'video' ? 1024 : 10}
              value={localContent.fileUrl}
              onSelect={(url: string) => updateContent({ fileUrl: url })}
              onSelectFile={handleFileSelect}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label">File Name</label>
          <div className="col-sm-9">
            <input 
              type="text" 
              className="form-control" 
              value={localContent.fileName} 
              onChange={(e) => updateContent({ fileName: e.target.value })} 
              placeholder="Display name for the file" 
            />
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label">Description</label>
          <div className="col-sm-9">
            <textarea
              className="form-control"
              rows={3}
              value={localContent.description || ''}
              onChange={(e) => updateContent({ description: e.target.value })}
              placeholder="Optional description of the file content"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label">Allow Download</label>
          <div className="col-sm-9">
            <input 
              type="checkbox" 
              checked={localContent.allowDownload} 
              onChange={(e) => updateContent({ allowDownload: e.target.checked })} 
            />
            <span className="ml-2">Allow users to download this file</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilePageEditor;